<div class="p-6 max-w-7xl mx-auto">
  <header class="mb-6">
    <h1 class="text-2xl font-semibold text-gray-900">Vendor ranking</h1>
    <p class="text-sm text-gray-500 mt-1">
      Multi-criteria ranking: price (40%), BOQ deviation (35%), timeliness (15%), track record (10%)
    </p>
  </header>

  <div *ngIf="loading" class="py-12 text-center text-gray-500">
    <mat-spinner diameter="36" class="mx-auto"></mat-spinner>
    <p class="mt-3">Loading rankings…</p>
  </div>

  <ng-container *ngIf="!loading">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      <!-- Org-wide rankings -->
      <section class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h2 class="font-semibold text-gray-900">Organization vendor leaderboard</h2>
          <p class="text-xs text-gray-500 mt-0.5">Aggregated across all BOQ bidding rounds</p>
        </div>
        <div class="overflow-x-auto">
          <table class="min-w-full text-sm" *ngIf="orgRankings.length; else noOrgRank">
            <thead class="text-left text-xs text-gray-500 uppercase bg-white">
              <tr>
                <th class="px-4 py-3">#</th>
                <th class="px-4 py-3">Vendor</th>
                <th class="px-4 py-3 text-right">Score</th>
                <th class="px-4 py-3 text-right">Bids</th>
                <th class="px-4 py-3 text-right">Awards</th>
                <th class="px-4 py-3 text-right">Win %</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let r of orgRankings" class="border-t border-gray-100 hover:bg-gray-50">
                <td class="px-4 py-3 font-bold" [class.text-emerald-600]="r.rank === 1">{{ r.rank }}</td>
                <td class="px-4 py-3 font-medium">{{ r.org_name }}</td>
                <td class="px-4 py-3 text-right">
                  <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold"
                    [class.bg-emerald-100]="r.composite_score >= 70"
                    [class.text-emerald-800]="r.composite_score >= 70"
                    [class.bg-amber-100]="r.composite_score >= 50 && r.composite_score < 70"
                    [class.text-amber-800]="r.composite_score >= 50 && r.composite_score < 70"
                    [class.bg-gray-100]="r.composite_score < 50"
                    [class.text-gray-700]="r.composite_score < 50">
                    {{ r.composite_score }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right">{{ r.bid_count }}</td>
                <td class="px-4 py-3 text-right">{{ r.award_count }}</td>
                <td class="px-4 py-3 text-right">{{ r.win_rate_pct }}%</td>
              </tr>
            </tbody>
          </table>
          <ng-template #noOrgRank>
            <p class="p-6 text-sm text-gray-500 text-center">No vendor bid history yet.</p>
          </ng-template>
        </div>
      </section>

      <!-- Per-invitation selector -->
      <section class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div class="px-5 py-4 border-b border-gray-100 bg-gray-50">
          <h2 class="font-semibold text-gray-900">Rank bidders for invitation</h2>
        </div>
        <div class="p-5">
          <mat-form-field appearance="outline" class="w-full">
            <mat-label>Select bidding invitation</mat-label>
            <mat-select [value]="selectedInvitationId" (selectionChange)="onInvitationChange($event.value)">
              <mat-option [value]="null">— Select —</mat-option>
              <mat-option *ngFor="let inv of invitations" [value]="inv.id">
                {{ inv.project_name }} · {{ inv.boq_code }} ({{ inv.bid_count }} bids)
              </mat-option>
            </mat-select>
          </mat-form-field>

          <div *ngIf="selectedInvitationMeta" class="mt-4 p-4 rounded-lg bg-blue-50 text-sm text-blue-900">
            <p><strong>Project:</strong> {{ selectedInvitationMeta.project_name }}</p>
            <p><strong>BOQ:</strong> {{ selectedInvitationMeta.boq_code }} — {{ selectedInvitationMeta.boq_name }}</p>
            <p><strong>Estimated total:</strong> {{ formatCurrency(selectedInvitationMeta.estimated_total) }}</p>
            <button mat-stroked-button class="mt-2" type="button" (click)="viewBids(selectedInvitationMeta.id)">
              View submitted bids
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- Invitation rankings detail -->
    <section *ngIf="invitationRankings.length" class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      <div class="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
        <h2 class="font-semibold text-gray-900">Bidder rankings</h2>
        <span class="text-xs text-gray-500">{{ invitationRankings.length }} bidder(s)</span>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full text-sm">
          <thead class="text-left text-xs text-gray-500 uppercase">
            <tr>
              <th class="px-4 py-3">Rank</th>
              <th class="px-4 py-3">Bidder</th>
              <th class="px-4 py-3 text-right">Bid amount</th>
              <th class="px-4 py-3 text-right">Deviation</th>
              <th class="px-4 py-3">Score breakdown</th>
              <th class="px-4 py-3 text-right">Composite</th>
              <th class="px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let r of invitationRankings" class="border-t border-gray-100"
              [class.bg-emerald-50]="r.rank === 1">
              <td class="px-4 py-3">
                <span class="inline-flex w-7 h-7 items-center justify-center rounded-full font-bold text-sm"
                  [class.bg-emerald-600]="r.rank === 1"
                  [class.text-white]="r.rank === 1"
                  [class.bg-gray-200]="r.rank !== 1"
                  [class.text-gray-700]="r.rank !== 1">
                  {{ r.rank }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="font-medium">{{ r.org_name }}</div>
                <div *ngIf="r.email" class="text-xs text-gray-500">{{ r.email }}</div>
              </td>
              <td class="px-4 py-3 text-right font-medium">{{ formatCurrency(r.bid_amount) }}</td>
              <td class="px-4 py-3 text-right">
                <span [class.text-red-600]="r.total_deviation_pct > 10"
                  [class.text-emerald-600]="r.total_deviation_pct <= 5">
                  {{ r.total_deviation_pct }}%
                </span>
              </td>
              <td class="px-4 py-3 min-w-[200px]">
                <div *ngFor="let key of scoreKeys(r.scores)" class="flex items-center gap-2 mb-1">
                  <span class="text-[10px] text-gray-500 w-20">{{ scoreLabels[key] || key }}</span>
                  <div class="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div class="h-full rounded-full" [ngClass]="scoreBarClass(r.scores[key])"
                      [style.width.%]="r.scores[key]"></div>
                  </div>
                  <span class="text-[10px] w-8 text-right">{{ r.scores[key] | number:'1.0-0' }}</span>
                </div>
              </td>
              <td class="px-4 py-3 text-right font-bold text-lg">{{ r.composite_score }}</td>
              <td class="px-4 py-3 capitalize text-xs">{{ r.status || 'submitted' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </ng-container>
</div>
